import { MenuAction } from '@anecdotes/design-system/dropdown-menu';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { RegularDateFormatMMMdy } from '@anecdotes/helpers';
import { REVOKED_BY_PROFILE_DELETED } from '../../../../constants';
import {
  AutoApprove,
  TrustCenterFacadeService,
  UserResponse,
  UserStatus,
  AutoRevoked,
  CompanyResponse,
} from '@anecdotes/shared-trust-center';
import { TrustCenterModalService } from '../../../../services/trustcenter-modals/trustcenter-modals.service';
import { firstValueFrom, map, Observable, of, shareReplay } from 'rxjs';
import { getEmailDomain } from '@anecdotes/utils';

@Component({
  selector: 'app-access-manager-item',
  templateUrl: './access-manager-item.component.html',
  styleUrls: ['./access-manager-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessManagerItemComponent implements OnChanges, OnInit {
  readonly UserStatus = UserStatus;
  readonly dateFormat = RegularDateFormatMMMdy;

  readonly resendLoginLinkAction: MenuAction = {
    icon: 'send_review_dark',
    id: 'resend-login-link',
    translationKey: this.buildTranslationKey('resendLoginLink'),
    action: () => this.resendLoginLink(),
  };

  readonly revokeAccessAction: MenuAction = {
    icon: 'X',
    id: 'revoke-access',
    translationKey: this.buildTranslationKey('revokeAccess'),
    action: () => this.revokeAccess(),
  };

  readonly approveWithoutNdaAction: MenuAction = {
    icon: 'add_users_dark',
    id: 'approve-without-nda',
    translationKey: this.buildTranslationKey('approveWithoutNda'),
    action: () => this.approveWithoutNda(),
  };

  readonly deleteAction: MenuAction = {
    icon: 'remove',
    id: 'delete',
    translationKey: this.buildTranslationKey('delete'),
    action: () => this.deleteUser(),
  };

  readonly manageAccessAction: MenuAction = {
    icon: 'access-request',
    id: 'manage-access',
    translationKey: this.buildTranslationKey('manageAccess'),
    action: () => this.openViewRequestSidePanel(),
  };

  automaticallyApprovedBy$: Observable<{ username: string; timestamp: Date }>;
  company$: Observable<CompanyResponse>;

  @Input()
  request: UserResponse;

  private get isAutomaticallyRevoked(): boolean {
    return this.isRevoked && this.request.revoked_by === AutoRevoked;
  }

  private get isApproved(): boolean {
    return this.request.status === UserStatus.Approved;
  }

  get isRevoked(): boolean {
    return this.request.status === UserStatus.Revoked;
  }

  get isAutomaticallyApproved(): boolean {
    return this.isApproved && this.request.approver_or_denier === AutoApprove;
  }

  get statusColor(): string {
    switch (this.request.status) {
      case UserStatus.Approved:
        return 'bg-blue-60';
      case UserStatus.PendingNda:
        return 'bg-orange-60';
      case UserStatus.Denied:
        return 'bg-pink-60';
      case UserStatus.Revoked:
        return 'bg-pink-80';
      default:
        return '';
    }
  }

  get statusTooltipKey(): string {
    switch (this.request.status) {
      case UserStatus.Approved: {
        switch (true) {
          case this.isAutomaticallyApproved:
            return this.buildTranslationKey('statusTooltip.automaticallyApproved');
          case !!this.request.nda_signed_at:
            return this.buildTranslationKey('statusTooltip.approvedWithNda');
          default:
            return this.buildTranslationKey('statusTooltip.approvedWithoutNda');
        }
      }
      case UserStatus.PendingNda:
        return this.buildTranslationKey('statusTooltip.pendingNda');
      case UserStatus.Denied:
        return this.buildTranslationKey('statusTooltip.denied');
      case UserStatus.Revoked: {
        let translationKey: string;

        switch (true) {
          case this.isAutomaticallyRevoked:
            translationKey = 'statusTooltip.automaticallyRevoked';
            break;
          case this.isRevokedByProfileDeleted:
            translationKey = 'statusTooltip.revokedByProfileDelete';
            break;
          default:
            translationKey = 'statusTooltip.revoked';
        }

        return this.buildTranslationKey(translationKey);
      }
    }
  }

  get menuActions(): MenuAction[] {
    switch (this.request.status) {
      case UserStatus.Approved:
        return [this.resendLoginLinkAction, this.revokeAccessAction, this.deleteAction];

      case UserStatus.PendingNda:
        return [this.resendLoginLinkAction, this.revokeAccessAction, this.deleteAction];

      case UserStatus.Denied:
        return [this.deleteAction];

      case UserStatus.Revoked:
        return [this.manageAccessAction, this.deleteAction];
    }
  }

  get fullName(): string {
    return `${this.request.first_name} ${this.request.last_name}`;
  }

  get isRevokedByProfileDeleted(): boolean {
    return this.request.revoked_by === REVOKED_BY_PROFILE_DELETED;
  }

  constructor(
    private cd: ChangeDetectorRef,
    private trustCenterFacadeService: TrustCenterFacadeService,
    private trustCenterModalService: TrustCenterModalService,
  ) {}

  ngOnInit(): void {
    this.company$ = this.trustCenterFacadeService.getCompanyById(this.request.company_id);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('request' in changes) {
      this.initializeAutoApprover();
    }
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  async openCompanySidePanel(): Promise<void> {
    const company = await firstValueFrom(this.company$);
    this.trustCenterModalService.openCompanySidePanel(company);
  }

  private async resendLoginLink(): Promise<void> {
    await this.trustCenterFacadeService.resendLoginLink(this.request.email);
    this.cd.detectChanges();
  }

  private async revokeAccess(): Promise<void> {
    await this.trustCenterFacadeService.revokeUser(this.request.email);
    this.cd.detectChanges();
  }

  private openViewRequestSidePanel(): void {
    this.trustCenterModalService.openAccessRequestSidePanel(this.request);
  }

  private async deleteUser(): Promise<void> {
    await this.trustCenterFacadeService.deleteUser(this.request.email);
    this.cd.detectChanges();
  }

  private async approveWithoutNda(): Promise<void> {
    const company = await firstValueFrom(this.company$);

    await this.trustCenterFacadeService.approveUser(this.request.email, company);
    this.cd.detectChanges();
  }

  private initializeAutoApprover(): void {
    if (this.isAutomaticallyApproved) {
      const domain = getEmailDomain(this.request.email);

      this.automaticallyApprovedBy$ = this.trustCenterFacadeService.getNonNewUserRequests().pipe(
        map((users) => {
          const domainUsers = users.filter(({ email }) => getEmailDomain(email) === domain);

          const approvedBy = domainUsers.reduce((earliest, current) => {
            return new Date(current.status_date) < new Date(earliest.status_date) ? current : earliest;
          });

          return {
            username: `${approvedBy.first_name} ${approvedBy.last_name}`,
            timestamp: approvedBy.status_date,
          };
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
    } else {
      this.automaticallyApprovedBy$ = of(undefined);
    }
  }
}
